import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/auth/useAuth';
import { ROUTES } from '@/lib/routes';

/**
 * Renders at `/` (and bare `/orgs`) for authenticated users and redirects
 * to the active organization's default app page (`/orgs/:orgSlug/runs`).
 *
 * Resolution order:
 * 1. Active membership picked by `useAuth` (seeded from localStorage and
 *    the server-side `activeOrganizationId` preference).
 * 2. First available membership.
 * 3. Otherwise (user genuinely has no memberships) send them to
 *    `/profile` so they can at least see their account page instead of
 *    looping back here forever.
 */
export function OrgRootRedirect() {
  const { activeMembership, memberships, loading } = useAuth();

  if (loading) {
    return (
      <div
        className="flex min-h-dvh items-center justify-center bg-background text-muted-foreground"
        data-testid="org-root-redirect-loading"
      >
        Loading&hellip;
      </div>
    );
  }

  const slug = activeMembership?.organization.slug ?? memberships[0]?.organization.slug ?? null;

  if (!slug) {
    return <Navigate to={ROUTES.profile} replace />;
  }

  return <Navigate to={ROUTES.org(slug).runs} replace />;
}
